import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/config';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Send, User, Shield } from 'lucide-react';
import { toast } from 'sonner';

const TicketDetail = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (ticketId) {
      fetchTicketDetails();
      fetchMessages();
    }
  }, [ticketId]);

  const fetchTicketDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTicket(response.data);
    } catch (error) {
      console.error('Error fetching ticket:', error);
      toast.error('Failed to load ticket');
      navigate('/vendor/dashboard/support');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tickets/${ticketId}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/tickets/${ticketId}/messages`,
        { message: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNewMessage('');
      toast.success('Message sent');
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-100 text-blue-700',
      pending: 'bg-yellow-100 text-yellow-700',
      in_progress: 'bg-purple-100 text-purple-700',
      resolved: 'bg-green-100 text-green-700',
      closed: 'bg-gray-100 text-gray-700'
    };
    return styles[status] || styles.open;
  };

  if (loading) {
    return <div className="text-center py-12">Loading ticket...</div>;
  }

  if (!ticket) {
    return <div className="text-center py-12">Ticket not found</div>;
  }

  return (
    <div className="max-w-4xl">
      <Button
        variant="ghost"
        onClick={() => navigate('/vendor/dashboard/support')}
        className="mb-6"
        data-testid="back-to-tickets-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Tickets
      </Button>

      {/* Ticket Header */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-2xl" data-testid="ticket-detail-subject">{ticket.subject}</CardTitle>
              <CardDescription className="mt-2">
                <div className="flex items-center gap-2">
                  <Badge className={getStatusBadge(ticket.status)} data-testid="ticket-detail-status">
                    {ticket.status.replace('_', ' ').toUpperCase()}
                  </Badge>
                  <Badge className="bg-blue-100 text-blue-700">
                    {ticket.ticket_type.replace('_', ' ')}
                  </Badge>
                </div>
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">Description</h3>
              <p className="text-gray-600" data-testid="ticket-detail-body">{ticket.body}</p>
            </div>
            <div className="flex gap-4 text-sm text-gray-500">
              <span>Created: {new Date(ticket.created_at).toLocaleString()}</span>
              {ticket.updated_at !== ticket.created_at && (
                <span>Updated: {new Date(ticket.updated_at).toLocaleString()}</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Messages */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Conversation</CardTitle>
        </CardHeader>
        <CardContent>
          {messages.length === 0 ? (
            <p className="text-center text-gray-500 py-8" data-testid="no-messages">No messages yet. Add a message below.</p>
          ) : (
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`p-4 rounded-lg ${
                    message.author_role === 'admin'
                      ? 'bg-green-50 border-l-4 border-green-500'
                      : 'bg-gray-50 border-l-4 border-blue-500'
                  }`}
                  data-testid={`message-${message.id}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {message.author_role === 'admin' ? (
                      <Shield className="w-4 h-4 text-green-600" />
                    ) : (
                      <User className="w-4 h-4 text-blue-600" />
                    )}
                    <span className="font-semibold text-sm">
                      {message.author_role === 'admin' ? 'Admin' : 'You'}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(message.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-gray-700" data-testid={`message-content-${message.id}`}>{message.message}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Message */}
      {!['resolved', 'closed'].includes(ticket.status) && (
        <Card>
          <CardHeader>
            <CardTitle>Add Message</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSendMessage} className="space-y-4">
              <Textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                rows={4}
                required
                data-testid="message-input"
              />
              <Button
                type="submit"
                className="w-full bg-green-600 hover:bg-green-700"
                disabled={sending || !newMessage.trim()}
                data-testid="send-message-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {sending ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TicketDetail;